import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import './Msp.css';
import './ExternalScanDetail.css';

/* ── helpers ──────────────────────────────────────────────────────── */

const severityColor = (s) => {
  if (s >= 9) return '#ff4d6d';
  if (s >= 7) return '#ff7a59';
  if (s >= 4) return '#f5c542';
  return '#00f3ff';
};

const severityLabel = (s) => {
  if (s >= 9) return 'Critical';
  if (s >= 7) return 'High';
  if (s >= 4) return 'Medium';
  if (s > 0) return 'Low';
  return 'Info';
};

const portColor = (svc) => {
  const map = {
    ssh: '#ff7a59', ftp: '#ff4d6d', telnet: '#ff4d6d', 'ms-wbt-server': '#ff4d6d',
    http: '#00f3ff', https: '#00f3ff', smtp: '#f5c542', mysql: '#a78bfa',
    postgresql: '#a78bfa', 'microsoft-ds': '#ff4d6d', redis: '#a78bfa',
    mongodb: '#a78bfa', vnc: '#ff7a59', 'vnc-http': '#ff7a59',
  };
  return map[(svc || '').toLowerCase()] || '#8892a4';
};

/* ── component ────────────────────────────────────────────────────── */

export default function ExternalScanDetail() {
  const { tenantId, scanId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIp, setActiveIp] = useState(null);
  const [expandedExposures, setExpandedExposures] = useState({});
  const [baselineStatus, setBaselineStatus] = useState('idle');

  useEffect(() => {
    setLoading(true);
    tenantApi
      .getExternalScanDetail(tenantId, scanId)
      .then((res) => {
        setData(res.data);
        if (res.data.ips && res.data.ips.length > 0) {
          setActiveIp(res.data.ips[0].ip);
        }
      })
      .catch((err) => setError(err.message || 'Failed to load scan detail'))
      .finally(() => setLoading(false));
  }, [tenantId, scanId]);

  const toggleExposure = (id) => {
    setExpandedExposures((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <div className="msp-page"><div className="msp-empty">Loading scan details...</div></div>;
  if (error) return <div className="msp-page"><div className="msp-empty" style={{ color: '#ff4d6d' }}>{error}</div></div>;
  if (!data) return null;

  const { scan, tenant, summary, domains, domain_ip_map, ips, exposures, web_technologies, diff } = data;
  const activeHost = ips.find((h) => h.ip === activeIp) || ips[0] || null;

  const handleDownloadReport = async () => {
    try {
      const response = await tenantApi.exportExternalScanReport(tenantId, scanId, 'pdf');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `External_Scan_${scanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  const handleSetBaseline = async () => {
    setBaselineStatus('submitting');
    try {
      await tenantApi.setBaselineScan(tenantId, scanId);
      setData((prev) => prev && { ...prev, scan: { ...prev.scan, is_baseline: true } });
      setBaselineStatus('idle');
    } catch (err) {
      console.error('Set baseline failed', err);
      setBaselineStatus('error');
    }
  };

  return (
    <div className="msp-page scan-detail-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="msp-header">
        <div>
          <h1>Scan #{scan.id} &mdash; {tenant.name}</h1>
          <p className="msp-subtitle">
            {scan.status === 'completed' ? 'Completed' : scan.status}{' '}
            {scan.completed_at && <> &middot; {new Date(scan.completed_at).toLocaleString()}</>}
            {scan.is_baseline && (
              <span className="msp-badge" style={{ marginLeft: '8px', background: 'var(--neon-cyan)', color: '#000' }}>Baseline</span>
            )}
          </p>
          {scan.comparison_type && scan.comparison_scan_id && (
            <p className="msp-subtitle" style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '4px' }}>
              Comparing against {scan.comparison_type === 'baseline' ? 'baseline' : 'previous'} scan #{scan.comparison_scan_id}
            </p>
          )}
        </div>
        <div className="msp-header-actions">
          <Link to={`/msp/tenants/${tenantId}/external-scans`} className="btn btn-secondary">
            Back to Scans
          </Link>
          {scan.status === 'completed' && !scan.is_baseline && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSetBaseline}
              disabled={baselineStatus === 'submitting'}
              style={{ marginRight: '8px' }}
            >
              {baselineStatus === 'submitting' ? 'Setting…' : 'Set as baseline'}
            </button>
          )}
          {scan.status === 'completed' && (
            <button className="btn btn-primary" onClick={handleDownloadReport}>
              Download Report
            </button>
          )}
        </div>
      </div>

      {/* ── Summary Cards ──────────────────────────────────────── */}
      <div className="scan-summary-grid">
        <div className="scan-summary-card">
          <span className="scan-summary-label">Risk Score</span>
          <span className="scan-summary-value" style={{ color: severityColor(scan.risk_score || 0) }}>
            {typeof scan.risk_score === 'number' ? scan.risk_score.toFixed(1) : '--'}
          </span>
        </div>
        <div className="scan-summary-card">
          <span className="scan-summary-label">Exposures</span>
          <span className="scan-summary-value">{summary.total_exposures}</span>
          <span className="scan-summary-breakdown">
            <span style={{ color: '#ff4d6d' }}>{summary.critical} critical</span>
            {' / '}
            <span style={{ color: '#ff7a59' }}>{summary.high} high</span>
          </span>
        </div>
        <div className="scan-summary-card">
          <span className="scan-summary-label">Open Ports</span>
          <span className="scan-summary-value">{summary.open_ports}</span>
          <span className="scan-summary-breakdown">{summary.total_ports} scanned</span>
        </div>
        <div className="scan-summary-card">
          <span className="scan-summary-label">IPs Discovered</span>
          <span className="scan-summary-value">{ips.length}</span>
        </div>
        <div className="scan-summary-card">
          <span className="scan-summary-label">Domains</span>
          <span className="scan-summary-value">{domains.length}</span>
          <span className="scan-summary-breakdown">{domains.join(', ')}</span>
        </div>
      </div>

      {/* ── Changes vs Baseline ──────────────────────────────────────── */}
      {diff && (scan.comparison_type || scan.comparison_scan_id) && (
        <div className="scan-section">
          <h2 className="scan-section-title">
            Changes vs {scan.comparison_type === 'baseline' ? 'Baseline' : 'Previous'} Scan #{scan.comparison_scan_id}
          </h2>
          <div className="scan-diff-summary">
            {typeof diff.risk_score_delta === 'number' && diff.risk_score_delta !== 0 && (
              <p>
                Risk score {diff.risk_score_delta > 0 ? 'increased' : 'decreased'} by {Math.abs(diff.risk_score_delta).toFixed(1)} since {scan.comparison_type === 'baseline' ? 'baseline' : 'previous scan'}.
              </p>
            )}
            <p>
              {Array.isArray(diff.new) ? diff.new.length : 0} new exposure{(Array.isArray(diff.new) ? diff.new.length : 0) !== 1 ? 's' : ''}
              {' · '}
              {Array.isArray(diff.removed) ? diff.removed.length : 0} resolved
              {' · '}
              {Array.isArray(diff.changed) ? diff.changed.length : 0} changed
            </p>
            {(Array.isArray(diff.new) && diff.new.length > 0) && (
              <div className="scan-diff-list">
                <h3>New Exposures</h3>
                <ul>
                  {diff.new.map((e, i) => (
                    <li key={i}>{e.asset_label || e.asset_type || 'Asset'} — {e.exposure_type || 'exposure'} (severity {typeof e.severity_score === 'number' ? e.severity_score.toFixed(1) : '—'})</li>
                  ))}
                </ul>
              </div>
            )}
            {(Array.isArray(diff.removed) && diff.removed.length > 0) && (
              <div className="scan-diff-list">
                <h3>Resolved (No Longer Detected)</h3>
                <ul>
                  {diff.removed.map((e, i) => (
                    <li key={i}>{e.asset_label || e.asset_type || 'Asset'} — {e.exposure_type || 'exposure'}</li>
                  ))}
                </ul>
              </div>
            )}
            {(Array.isArray(diff.changed) && diff.changed.length > 0) && (
              <div className="scan-diff-list">
                <h3>Changed Exposures</h3>
                <ul>
                  {diff.changed.map((c, i) => {
                    const after = c.after || c;
                    return (
                      <li key={i}>
                        {after.asset_label || after.asset_type || 'Asset'} — {after.exposure_type || 'exposure'}
                        {c.changes?.severity_score && (
                          <span> (severity {c.changes.severity_score.before} → {c.changes.severity_score.after})</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {(!diff.new?.length && !diff.removed?.length && !diff.changed?.length && (typeof diff.risk_score_delta !== 'number' || diff.risk_score_delta === 0)) && (
              <p className="scan-diff-empty">No changes detected.</p>
            )}
          </div>
        </div>
      )}

      {/* Scan intensity note - explains port coverage variability */}
      <div className="scan-intensity-note">
        <strong>Scan intensity:</strong> {(scan.intensity || 'standard').toLowerCase()}
        {(scan.intensity || 'standard').toLowerCase() === 'light' && ' (top 100 ports only)'}
        {(scan.intensity || 'standard').toLowerCase() === 'standard' && ' (top 1000 ports)'}
        {(scan.intensity || 'standard').toLowerCase() === 'deep' && ' (full 1–65535 port range)'}
        . Different intensities and timing can affect which ports are detected (e.g. dev ports 3000, 5001 may only appear in deep scans or when services were running).
      </div>

      {/* ── Domain/IP Map ──────────────────────────────────────── */}
      {Object.keys(domain_ip_map).length > 0 && (
        <div className="scan-section">
          <h2 className="scan-section-title">DNS Resolution Map</h2>
          <div className="dns-map-grid">
            {Object.entries(domain_ip_map).map(([domain, ipList]) => (
              <div key={domain} className="dns-map-item">
                <span className="dns-domain">{domain}</span>
                <span className="dns-arrow">&rarr;</span>
                <div className="dns-ips">
                  {ipList.map((ip) => (
                    <button
                      key={ip}
                      className={`dns-ip-badge ${activeIp === ip ? 'active' : ''}`}
                      onClick={() => setActiveIp(ip)}
                    >
                      {ip}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Host Panel (Shodan-style) ─────────────────────────── */}
      {ips.length > 0 && (
        <div className="scan-section">
          <h2 className="scan-section-title">Host Details</h2>
          <div className="host-layout">
            {/* IP sidebar */}
            <div className="host-sidebar">
              {ips.map((host) => (
                <button
                  key={host.ip}
                  className={`host-ip-btn ${activeIp === host.ip ? 'active' : ''}`}
                  onClick={() => setActiveIp(host.ip)}
                >
                  <span className="host-ip-label">{host.ip}</span>
                  <span className="host-port-count">
                    {host.ports.length} port{host.ports.length !== 1 ? 's' : ''}
                  </span>
                </button>
              ))}
            </div>

            {/* Active host detail */}
            {activeHost && (
              <div className="host-detail">
                <div className="host-meta-row">
                  <div className="host-meta-item">
                    <span className="host-meta-key">IP</span>
                    <span className="host-meta-val">{activeHost.ip}</span>
                  </div>
                  {activeHost.domains.length > 0 && (
                    <div className="host-meta-item">
                      <span className="host-meta-key">Domains</span>
                      <span className="host-meta-val">{activeHost.domains.join(', ')}</span>
                    </div>
                  )}
                  {activeHost.os && (
                    <div className="host-meta-item">
                      <span className="host-meta-key">OS</span>
                      <span className="host-meta-val">{activeHost.os}</span>
                    </div>
                  )}
                  {activeHost.geo?.country && (
                    <div className="host-meta-item">
                      <span className="host-meta-key">Location</span>
                      <span className="host-meta-val">
                        {[activeHost.geo.city, activeHost.geo.country].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {activeHost.geo?.org && (
                    <div className="host-meta-item">
                      <span className="host-meta-key">Organization</span>
                      <span className="host-meta-val">{activeHost.geo.org}</span>
                    </div>
                  )}
                  {activeHost.geo?.asn && (
                    <div className="host-meta-item">
                      <span className="host-meta-key">ASN</span>
                      <span className="host-meta-val">{activeHost.geo.asn}</span>
                    </div>
                  )}
                </div>

                {/* Port badges */}
                <div className="port-badges-row">
                  {activeHost.ports.length === 0 ? (
                    <span className="scan-muted">No open ports detected</span>
                  ) : (
                    activeHost.ports.map((p, idx) => (
                      <span
                        key={idx}
                        className="port-badge"
                        style={{ borderColor: portColor(p.service) }}
                      >
                        <span className="port-num">{p.port}</span>
                        <span className="port-svc">{p.service || '?'}</span>
                      </span>
                    ))
                  )}
                </div>

                {/* Service detail cards */}
                {activeHost.ports.length > 0 && (
                  <div className="service-cards">
                    {activeHost.ports.map((p, idx) => (
                      <div key={idx} className="service-card">
                        <div className="service-card-header">
                          <span className="service-port" style={{ color: portColor(p.service) }}>
                            {p.port}/{p.protocol}
                          </span>
                          <span className="service-name">{p.service || 'unknown'}</span>
                          <span
                            className="service-risk"
                            style={{ color: severityColor(p.risk_score) }}
                          >
                            {severityLabel(p.risk_score)} ({p.risk_score.toFixed(1)})
                          </span>
                        </div>
                        {(p.product || p.version) && (
                          <div className="service-version">
                            {p.product} {p.version}
                          </div>
                        )}
                        {p.banner && (
                          <div className="service-banner">
                            <span className="banner-label">Banner</span>
                            <code>{p.banner}</code>
                          </div>
                        )}
                        {p.cpe && (
                          <div className="service-cpe">
                            <span className="cpe-label">CPE</span>
                            <code>{p.cpe}</code>
                          </div>
                        )}
                        {p.vulns && p.vulns.length > 0 && (
                          <div className="service-vulns">
                            <span className="vulns-label">Vulnerabilities ({p.vulns.length})</span>
                            <div className="vuln-list">
                              {p.vulns.map((v, vi) => (
                                <div key={vi} className="vuln-item">
                                  <a
                                    href={`https://nvd.nist.gov/vuln/detail/${v.cve}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="vuln-cve"
                                  >
                                    {v.cve}
                                  </a>
                                  {v.cvss != null && (
                                    <span
                                      className="vuln-cvss"
                                      style={{ color: severityColor(v.cvss) }}
                                    >
                                      CVSS {v.cvss}
                                    </span>
                                  )}
                                  {v.summary && <span className="vuln-desc">{v.summary}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Web Technologies ───────────────────────────────────── */}
      {web_technologies && web_technologies.length > 0 && (
        <div className="scan-section">
          <h2 className="scan-section-title">Detected Technologies</h2>
          <div className="tech-badges">
            {web_technologies.map((t) => (
              <span key={t} className="tech-badge">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Exposures ──────────────────────────────────────────── */}
      <div className="scan-section">
        <h2 className="scan-section-title">
          Exposures ({exposures.length})
        </h2>
        {exposures.length === 0 ? (
          <div className="msp-empty">No exposures recorded for this scan.</div>
        ) : (
          <div className="exposure-list">
            {exposures.map((exp, idx) => {
              const key = exp.id || idx;
              const isOpen = expandedExposures[key];
              return (
                <div key={key} className="exposure-item">
                  <button
                    className="exposure-header"
                    onClick={() => toggleExposure(key)}
                  >
                    <span
                      className="exposure-severity-dot"
                      style={{ background: severityColor(exp.severity_score || 0) }}
                    />
                    <span className="exposure-title">
                      {exp.asset_label || exp.exposure_type}
                    </span>
                    <span className="exposure-type-badge">
                      {(exp.exposure_type || '').replace(/_/g, ' ')}
                    </span>
                    <span
                      className="exposure-score"
                      style={{ color: severityColor(exp.severity_score || 0) }}
                    >
                      {(exp.severity_score || 0).toFixed(1)}
                    </span>
                    <span className="exposure-urgency">
                      {exp.urgency || ''}
                    </span>
                    <span className="exposure-chevron">{isOpen ? '\u25B2' : '\u25BC'}</span>
                  </button>
                  {isOpen && (
                    <div className="exposure-body">
                      {exp.summary && (
                        <p className="exposure-summary">{exp.summary}</p>
                      )}
                      {exp.category && (
                        <div className="exposure-detail-row">
                          <span className="exposure-detail-key">Risk Category</span>
                          <span>{exp.category}</span>
                        </div>
                      )}
                      {exp.remediation && (
                        <div className="exposure-remediation">
                          <span className="exposure-detail-key">Remediation</span>
                          <p>{exp.remediation}</p>
                        </div>
                      )}
                      {exp.compliance_refs && exp.compliance_refs.length > 0 && (
                        <div className="exposure-compliance">
                          <span className="exposure-detail-key">Compliance</span>
                          <ul>
                            {exp.compliance_refs.map((ref, ri) => (
                              <li key={ri}>{ref}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {exp.first_seen && (
                        <div className="exposure-detail-row">
                          <span className="exposure-detail-key">First Seen</span>
                          <span>{new Date(exp.first_seen).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
